import { PrScreenSession } from "./PrScreenSession";
import { authorizeFinalDiscount, FinalDiscountRequest, FinalDiscountResult, invoiceFromFinalQuote } from "./FinalDiscountAuthorization";
import { CreateInvoiceFromBarcodeReadsRequest } from "@/application/contracts/app/erp/sales/CreateInvoiceFromBarcodeReadsRequest";
import { CreateInvoiceFromQuoteRequest } from "@/application/contracts/app/erp/sales/CreateInvoiceFromQuoteRequest";
import { DownloadSaleRequest } from "@/application/contracts/app/erp/sales/DownloadSaleRequest";
import { GetSalesRequest } from "@/application/contracts/app/erp/sales/GetSalesRequest";
import { PaySaleRequest } from "@/application/contracts/app/erp/sales/PaySaleRequest";
import { SendSaleRequest } from "@/application/contracts/app/erp/sales/SendSaleRequest";
import { BalanceDueDto } from "@/application/dtos/app/erp/v3/contacts/customers/BalanceDueDto";
import { SaleDto } from "@/application/dtos/app/erp/v3/sales/SaleDto";
import { ApiService, ApiServiceType } from "../../../ApiService";
import { DiscountAuditReport } from "./DiscountAudit";

export class SaleService extends ApiService {
  constructor() {
    super("Sales/Sale", ApiServiceType.TenantDatabase);
  }
  getAllSales(request: GetSalesRequest): Promise<SaleDto[]> {
    // return Promise.resolve(fakeDataSales);
    return super.getAll("GetAll?" + super.toQueryString(request));
  }
  getDiscountAudit(from: string, to: string): Promise<DiscountAuditReport> {
    return super.get("DiscountAudit?from=" + encodeURIComponent(from) + "&to=" + encodeURIComponent(to)).then(data => {
      if (!data || typeof data !== "object" || typeof data.policyConfigured !== "boolean" || !Array.isArray(data.invoices)) {
        throw new Error("No se recibió una respuesta válida de la auditoría. Recarga el sitio o contacta a Absys.");
      }
      return data;
    });
  }
  getAllOverdue(): Promise<SaleDto[]> {
    return super.getAll("GetAllOverdue");
  }
  getSale(type: string, folio: number): Promise<SaleDto> {
    return super.get(`Get/${type}/${folio}`);
  }
  getBalance(type: string, folio: number): Promise<BalanceDueDto> {
    return super.get(`GetBalance/${type}/${folio}`);
  }
  authorizeFinalDiscount(request: FinalDiscountRequest): Promise<boolean> {
    return authorizeFinalDiscount(
      data => super.post(data, "DiscountAuthorization") as Promise<FinalDiscountResult>,
      request,
      { confirm: message => window.confirm(message), prompt: message => window.prompt(message) }
    );
  }
  async createInvoiceFromBarcodeReads(data: CreateInvoiceFromBarcodeReadsRequest): Promise<SaleDto> {
    const session = data.type === "PR" ? null : new PrScreenSession();
    let finalClient: SaleService = this;
    try {
      return await invoiceFromFinalQuote(
        () => this.createFromBarcodeReads({ ...data, invoice: false, pay: false, createSaleLines: true, prepareInvoice: true }),
        async quote => {
          if (session) {
            if (!(await session.acquire(quote.folio))) throw new Error(session.message);
            finalClient = this.withPrScreen(session.screen);
          }
          return finalClient.authorizeFinalDiscount({ type: "PR", folio: quote.folio, fromReads: false });
        },
        quote => finalClient.createInvoice({ folio: quote.folio, incomeBankId: data.bankIncomeId,
          pay: data.pay, toPayDocument: data.toPayDocument, receiptNumber: "" })
      );
    } finally {
      if (session) await session.release();
    }
  }
  createInvoice(data: CreateInvoiceFromQuoteRequest): Promise<SaleDto> {
    return super.post(data, "CreateInvoice");
  }
  createFromBarcodeReads(data: CreateInvoiceFromBarcodeReadsRequest): Promise<SaleDto> {
    return super.post(data, "CreateFromBarcodeReads");
  }
  pay(data: PaySaleRequest): Promise<BalanceDueDto> {
    return super.post(data, "Pay");
  }
  download(request: DownloadSaleRequest): Promise<any> {
    return super.download(request, `Download`);
  }
  send(request: SendSaleRequest): Promise<any> {
    return super.post(request, `Send`);
  }
  deletePayments(type: string, folio: number, paymentFolio: number = 0): Promise<SaleDto> {
    return super.delete("", `DeletePayments/${type}/${folio}?paymentFolio=` + paymentFolio);
  }
  deleteSale(type: string, folio: number): Promise<SaleDto> {
    return super.delete("", `Delete/${type}/${folio}`);
  }
}
